import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Target,
  Award,
  TrendingUp,
  BarChart3,
  Loader2,
  Play,
  RotateCcw,
  ArrowRight,
  Sparkles,
  User,
  Mail,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SEO Meta component
const SEOHead = () => {
  useEffect(() => {
    document.title = "Free CIRO Practice Exam & Diagnostic Tool | Vitta Finance Academy";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Take our free CIRO practice exam diagnostic tool. Identify your weak areas for CIRE and RSE exams. Get personalized study recommendations to pass your Canadian securities exam.');
    }
  }, []);
  
  return null;
};

// User Registration Form Component
const UserRegistrationForm = ({ examType, examName, onSubmit, onBack, loading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px 14px 45px',
    border: '2px solid var(--border-light)',
    borderRadius: '10px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const inputErrorStyle = {
    ...inputStyle,
    borderColor: '#dc2626'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-light)',
      paddingTop: '40px',
      paddingBottom: '60px'
    }}>
      <div style={{
        maxWidth: '500px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: '0.95rem',
            marginBottom: '20px',
            padding: '8px 0'
          }}
        >
          <ChevronLeft size={18} />
          Back to Exam Selection
        </button>

        {/* Form Card */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px 30px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.08)'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              margin: '0 auto 20px'
            }}>
              <BookOpen size={32} />
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Start Your {examType} Diagnostic
            </h2>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: '0.95rem'
            }}>
              Enter your details to begin. We'll email you a detailed report of your results.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
              {/* First Name */}
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}>
                  First Name *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={18} style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-secondary)'
                  }} />
                  <input
                    type="text"
                    data-testid="first-name-input"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    style={errors.firstName ? inputErrorStyle : inputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = errors.firstName ? '#dc2626' : 'var(--border-light)'}
                  />
                </div>
                {errors.firstName && (
                  <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}>
                  Last Name *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={18} style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-secondary)'
                  }} />
                  <input
                    type="text"
                    data-testid="last-name-input"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    style={errors.lastName ? inputErrorStyle : inputStyle}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = errors.lastName ? '#dc2626' : 'var(--border-light)'}
                  />
                </div>
                {errors.lastName && (
                  <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                Email Address *
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)'
                }} />
                <input
                  type="email"
                  data-testid="email-input"
                  placeholder="john.doe@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={errors.email ? inputErrorStyle : inputStyle}
                  onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                  onBlur={(e) => e.target.style.borderColor = errors.email ? '#dc2626' : 'var(--border-light)'}
                />
              </div>
              {errors.email && (
                <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>{errors.email}</p>
              )}
            </div>

            {/* Organization */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                Organization <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(Optional)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <Building2 size={18} style={{
                  position: 'absolute',
                  left: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-secondary)'
                }} />
                <input
                  type="text"
                  data-testid="organization-input"
                  placeholder="ABC Financial Services"
                  value={formData.organization}
                  onChange={(e) => setFormData({...formData, organization: e.target.value})}
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="start-diagnostic-btn"
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: loading 
                  ? 'var(--text-secondary)'
                  : 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Loading Questions...
                </>
              ) : (
                <>
                  <Play size={20} />
                  Start Diagnostic
                </>
              )}
            </button>

            {/* Privacy Note */}
            <p style={{
              textAlign: 'center',
              color: 'var(--text-secondary)',
              fontSize: '0.8rem',
              marginTop: '20px'
            }}>
              Your information is secure. We'll only email you your diagnostic results and helpful study resources.
            </p>
          </form>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Exam Selection Component
const ExamSelector = ({ onSelectExam, examInfo, loading }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: 'var(--brand-primary)' }} />
        <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>Loading exam information...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--brand-primary) 0%, #1e40af 100%)',
        padding: '80px 20px',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5
        }} />
        
        <div style={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.15)',
            padding: '8px 20px',
            borderRadius: '30px',
            marginBottom: '24px',
            fontSize: '0.9rem'
          }}>
            <Sparkles size={16} />
            Free Diagnostic Tool
          </div>
          
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '20px',
            lineHeight: 1.2
          }}>
            CIRO Exam Diagnostic Tool
          </h1>
          
          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            opacity: 0.95,
            maxWidth: '600px',
            margin: '0 auto 30px',
            lineHeight: 1.6
          }}>
            Discover your strengths and weaknesses before your exam. Our diagnostic tool identifies exactly where you need to focus your study efforts.
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle2 size={20} />
              <span>30 Practice Questions</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Clock size={20} />
              <span>45 Minutes</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <BarChart3 size={20} />
              <span>Detailed Analysis</span>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Cards */}
      <section style={{
        padding: '60px 20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h2 style={{
          textAlign: 'center',
          fontSize: '1.75rem',
          fontWeight: 600,
          marginBottom: '40px',
          color: 'var(--text-primary)'
        }}>
          Select Your Exam
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '30px'
        }}>
          {examInfo?.exams?.map((exam) => (
            <div
              key={exam.id}
              data-testid={`exam-card-${exam.id.toLowerCase()}`}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid var(--border-light)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => onSelectExam(exam.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <BookOpen size={28} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: '4px'
                  }}>
                    {exam.id}
                  </h3>
                  <span style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)'
                  }}>
                    {exam.question_count} Questions Available
                  </span>
                </div>
              </div>
              
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                marginBottom: '20px'
              }}>
                {exam.description}
              </p>
              
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '20px'
              }}>
                {exam.topics.slice(0, 4).map((topic, idx) => (
                  <span
                    key={idx}
                    style={{
                      background: 'var(--bg-light)',
                      color: 'var(--text-secondary)',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '0.8rem'
                    }}
                  >
                    {topic}
                  </span>
                ))}
                {exam.topics.length > 4 && (
                  <span style={{
                    color: 'var(--brand-primary)',
                    padding: '6px 12px',
                    fontSize: '0.8rem'
                  }}>
                    +{exam.topics.length - 4} more
                  </span>
                )}
              </div>
              
              <button
                data-testid={`start-${exam.id.toLowerCase()}-exam-btn`}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                <Play size={18} />
                Start {exam.id} Diagnostic
              </button>
            </div>
          ))}
        </div>
        
        {/* Coming Soon */}
        {examInfo?.coming_soon?.length > 0 && (
          <div style={{
            marginTop: '40px',
            textAlign: 'center',
            padding: '30px',
            background: 'var(--bg-light)',
            borderRadius: '12px'
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '10px'
            }}>
              Coming Soon
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Diagnostic tools for {examInfo.coming_soon.join(' and ')} exams will be available soon!
            </p>
          </div>
        )}
      </section>

      {/* Benefits Section */}
      <section style={{
        padding: '60px 20px',
        background: 'var(--bg-light)'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '1.75rem',
            fontWeight: 600,
            marginBottom: '40px',
            color: 'var(--text-primary)'
          }}>
            Why Use Our Diagnostic Tool?
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '25px'
          }}>
            {[
              {
                icon: <Target size={28} />,
                title: "Identify Weak Areas",
                description: "Pinpoint exactly which topics need more attention before your exam."
              },
              {
                icon: <TrendingUp size={28} />,
                title: "Track Progress",
                description: "See how you improve over time with detailed performance metrics."
              },
              {
                icon: <Award size={28} />,
                title: "Exam-Style Questions",
                description: "Practice with questions that mirror the actual CIRO exam format."
              }
            ].map((benefit, idx) => (
              <div
                key={idx}
                style={{
                  background: 'white',
                  padding: '30px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  margin: '0 auto 20px'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  marginBottom: '10px',
                  color: 'var(--text-primary)'
                }}>
                  {benefit.title}
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.95rem',
                  lineHeight: 1.5
                }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Quiz Component
const QuizInterface = ({ examType, questions, onComplete, onBack }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(45 * 60); // 45 minutes
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  // Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  const handleAnswer = (optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionIndex
    }));
  };

  const handleSubmit = () => {
    const formattedAnswers = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
      question_id: questionId,
      selected_answer: selectedAnswer
    }));
    onComplete(formattedAnswers);
  };

  const isTimeWarning = timeRemaining <= 300; // 5 minutes

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-light)',
      paddingTop: '20px'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid var(--border-light)',
        padding: '15px 20px',
        position: 'sticky',
        top: '80px',
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}>
              {examType} Practice Exam
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Question {currentIndex + 1} of {questions.length}
            </p>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '8px',
              background: isTimeWarning ? '#fef2f2' : 'var(--bg-light)',
              color: isTimeWarning ? '#dc2626' : 'var(--text-primary)'
            }}>
              <Clock size={18} />
              <span style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '1.1rem' }}>
                {formatTime(timeRemaining)}
              </span>
            </div>
            
            <button
              onClick={onBack}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Exit
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div style={{
          maxWidth: '1000px',
          margin: '15px auto 0',
          height: '6px',
          background: 'var(--bg-light)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, var(--brand-primary) 0%, #3b82f6 100%)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Question */}
      <div style={{
        maxWidth: '800px',
        margin: '30px auto',
        padding: '0 20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          <div style={{
            display: 'inline-block',
            background: 'var(--bg-light)',
            color: 'var(--brand-primary)',
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '0.85rem',
            marginBottom: '20px'
          }}>
            {currentQuestion.topic}
          </div>
          
          <h3 style={{
            fontSize: '1.2rem',
            fontWeight: 500,
            color: 'var(--text-primary)',
            lineHeight: 1.6,
            marginBottom: '30px'
          }}>
            {currentQuestion.question}
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {currentQuestion.options.map((option, idx) => {
              const isSelected = answers[currentQuestion.id] === idx;
              return (
                <button
                  key={idx}
                  data-testid={`option-${idx}`}
                  onClick={() => handleAnswer(idx)}
                  style={{
                    padding: '18px 20px',
                    background: isSelected 
                      ? 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)'
                      : 'white',
                    color: isSelected ? 'white' : 'var(--text-primary)',
                    border: `2px solid ${isSelected ? 'transparent' : 'var(--border-light)'}`,
                    borderRadius: '12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}
                >
                  <span style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: isSelected ? 'rgba(255,255,255,0.2)' : 'var(--bg-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span>{option}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '30px',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            style={{
              padding: '14px 24px',
              background: currentIndex === 0 ? 'var(--bg-light)' : 'white',
              color: currentIndex === 0 ? 'var(--text-secondary)' : 'var(--text-primary)',
              border: '1px solid var(--border-light)',
              borderRadius: '10px',
              cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem'
            }}
          >
            <ChevronLeft size={18} />
            Previous
          </button>
          
          <div style={{
            display: 'flex',
            gap: '10px'
          }}>
            {currentIndex < questions.length - 1 ? (
              <button
                data-testid="next-question-btn"
                onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
                style={{
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Next
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                data-testid="submit-exam-btn"
                onClick={() => setShowConfirmSubmit(true)}
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Submit Exam
                <CheckCircle2 size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Question Navigator */}
        <div style={{
          marginTop: '30px',
          background: 'white',
          borderRadius: '12px',
          padding: '20px'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: 'var(--text-secondary)',
            marginBottom: '15px'
          }}>
            Question Navigator ({answeredCount}/{questions.length} answered)
          </p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {questions.map((q, idx) => {
              const isAnswered = answers[q.id] !== undefined;
              const isCurrent = idx === currentIndex;
              return (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    border: isCurrent ? '2px solid var(--brand-primary)' : '1px solid var(--border-light)',
                    background: isAnswered ? 'var(--brand-primary)' : 'white',
                    color: isAnswered ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 500
                  }}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '450px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: answeredCount < questions.length ? '#fef3c7' : '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              {answeredCount < questions.length ? (
                <AlertTriangle size={28} color="#d97706" />
              ) : (
                <CheckCircle2 size={28} color="#059669" />
              )}
            </div>
            
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              marginBottom: '10px'
            }}>
              {answeredCount < questions.length ? 'Some Questions Unanswered' : 'Ready to Submit?'}
            </h3>
            
            <p style={{
              color: 'var(--text-secondary)',
              marginBottom: '25px'
            }}>
              {answeredCount < questions.length 
                ? `You have answered ${answeredCount} of ${questions.length} questions. Unanswered questions will be marked incorrect.`
                : 'You have answered all questions. Submit your exam to see your results.'
              }
            </p>
            
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowConfirmSubmit(false)}
                style={{
                  padding: '12px 24px',
                  background: 'var(--bg-light)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Go Back
              </button>
              <button
                data-testid="confirm-submit-btn"
                onClick={handleSubmit}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Submit Exam
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Results Component
const ResultsDisplay = ({ results, examType, examInfo, onRetake }) => {
  const passed = results.passed;
  const scoreColor = passed ? '#059669' : '#dc2626';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-light)',
      paddingTop: '40px',
      paddingBottom: '60px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Score Card */}
        <div style={{
          background: passed 
            ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
            : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
          borderRadius: '20px',
          padding: '60px 30px 50px',
          textAlign: 'center',
          color: 'white',
          marginBottom: '30px'
        }}>
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 25px',
            border: '4px solid rgba(255,255,255,0.3)'
          }}>
            <span style={{ fontSize: '2.5rem', fontWeight: 700 }}>
              {Math.round(results.overall_score)}%
            </span>
          </div>
          
          <h2 style={{ fontSize: '1.75rem', fontWeight: 600, marginBottom: '10px' }}>
            {passed ? 'Congratulations!' : 'Keep Going!'}
          </h2>
          
          <p style={{ fontSize: '1.1rem', opacity: 0.95, marginBottom: '15px' }}>
            {passed 
              ? `You passed the ${examType} diagnostic exam!`
              : `You scored below the 70% passing threshold for ${examType}.`
            }
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '40px',
            marginTop: '20px'
          }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{results.total_correct}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Correct</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{results.total_questions - results.total_correct}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Incorrect</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>{results.total_questions}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Total</div>
            </div>
          </div>
        </div>

        {/* Recommendation */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            marginBottom: '15px',
            color: 'var(--text-primary)'
          }}>
            Our Recommendation
          </h3>
          <p style={{
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
            fontSize: '1rem'
          }}>
            {results.recommendation}
          </p>
        </div>

        {/* Weak Areas */}
        {results.weak_areas && results.weak_areas.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertTriangle size={20} color="#dc2626" />
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}>
                Areas Needing Improvement
              </h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {results.weak_areas.map((area, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#fef2f2',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: '4px solid #dc2626'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{
                      fontWeight: 600,
                      color: 'var(--text-primary)'
                    }}>
                      {area.category}
                    </h4>
                    <span style={{
                      background: '#dc2626',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: 600
                    }}>
                      {Math.round(area.score)}%
                    </span>
                  </div>
                  {area.missed_topics && area.missed_topics.length > 0 && (
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                      <strong>Focus on:</strong> {area.missed_topics.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strong Areas */}
        {results.strong_areas && results.strong_areas.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#d1fae5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle2 size={20} color="#059669" />
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}>
                Your Strong Areas
              </h3>
            </div>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              {results.strong_areas.map((area, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#d1fae5',
                    color: '#059669',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span style={{ fontWeight: 500 }}>{area.category}</span>
                  <span style={{ fontWeight: 700 }}>{Math.round(area.score)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        {results.categories && Object.keys(results.categories).length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              marginBottom: '25px',
              color: 'var(--text-primary)'
            }}>
              Performance by Category
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {Object.entries(results.categories).map(([category, data], idx) => {
                const percentage = data.score;
                const barColor = percentage >= 70 ? '#059669' : percentage >= 50 ? '#d97706' : '#dc2626';
                
                return (
                  <div key={idx}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem'
                      }}>
                        {category}
                      </span>
                      <span style={{
                        fontWeight: 600,
                        color: barColor
                      }}>
                        {data.correct}/{data.total} ({Math.round(percentage)}%)
                      </span>
                    </div>
                    <div style={{
                      height: '10px',
                      background: 'var(--bg-light)',
                      borderRadius: '5px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: barColor,
                        borderRadius: '5px',
                        transition: 'width 1s ease'
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CTA Section - Enhanced Subscription Promotion */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, var(--brand-primary) 50%, #1e40af 100%)',
          borderRadius: '20px',
          padding: '50px 30px',
          textAlign: 'center',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.8
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'var(--accent-gold)',
              color: 'var(--text-primary)',
              padding: '8px 20px',
              borderRadius: '30px',
              marginBottom: '20px',
              fontSize: '0.85rem',
              fontWeight: 700
            }}>
              <Sparkles size={16} />
              MONTHLY SUBSCRIPTION
            </div>
            
            <h3 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 700,
              marginBottom: '15px'
            }}>
              Ready to Master the {examType} Exam?
            </h3>
            
            <p style={{
              fontSize: '1.1rem',
              opacity: 0.95,
              marginBottom: '30px',
              maxWidth: '550px',
              margin: '0 auto 30px',
              lineHeight: 1.6
            }}>
              Get unlimited access to our comprehensive study materials, practice exams, and AI-powered tutoring to guarantee your success.
            </p>
            
            {/* Pricing Card */}
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              padding: '30px',
              maxWidth: '400px',
              margin: '0 auto 30px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: '5px',
                marginBottom: '10px'
              }}>
                <span style={{ fontSize: '3rem', fontWeight: 800 }}>$29.99</span>
                <span style={{ fontSize: '1.2rem', opacity: 0.8 }}>CAD/month</span>
              </div>
              
              <p style={{
                fontSize: '0.95rem',
                opacity: 0.9,
                marginBottom: '20px'
              }}>
                Cancel anytime • No commitment
              </p>
              
              {/* Features */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                textAlign: 'left',
                marginBottom: '25px'
              }}>
                {[
                  'Full-length mock exams with explanations',
                  'Video lessons by industry experts',
                  'AI-powered personalized tutoring',
                  'Progress tracking & weak area analysis',
                  'Mobile-friendly study materials'
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <CheckCircle2 size={18} color="#22c55e" />
                    <span style={{ fontSize: '0.95rem' }}>{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA Button */}
              <a 
                href={examInfo?.exams?.find(e => e.id === examType)?.course_url || '/courses'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <button
                  data-testid="subscribe-btn"
                  style={{
                    width: '100%',
                    padding: '18px 28px',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFC000 30%, #FFB000 70%, #E6A000 100%)',
                    color: '#1a1a2e',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1.15rem',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    boxShadow: '0 4px 25px rgba(255, 215, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 8px 35px rgba(255, 215, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 25px rgba(255, 215, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                  }}
                >
                  Start Your Subscription
                  <ArrowRight size={20} />
                </button>
              </a>
            </div>
            
            {/* Secondary Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              <Link to="/courses" style={{ textDecoration: 'none' }}>
                <button
                  data-testid="view-courses-btn"
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    color: 'white',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                  }}
                >
                  View All Courses
                </button>
              </Link>
              <button
                data-testid="retake-exam-btn"
                onClick={onRetake}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
              >
                <RotateCcw size={18} />
                Retake Diagnostic
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const DiagnosticTool = () => {
  const [stage, setStage] = useState('select'); // select, register, quiz, results
  const [examType, setExamType] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [results, setResults] = useState(null);
  const [examInfo, setExamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  // Fetch exam info on mount
  useEffect(() => {
    const fetchExamInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/diagnostic-tool/exam-info`);
        if (!response.ok) throw new Error('Failed to fetch exam info');
        const data = await response.json();
        setExamInfo(data);
      } catch (err) {
        console.error('Error fetching exam info:', err);
        setError('Failed to load exam information. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchExamInfo();
  }, []);

  const handleSelectExam = (type) => {
    setExamType(type);
    setStage('register');
  };

  const handleUserRegistration = async (formData) => {
    setLoading(true);
    setError(null);
    setUserInfo({
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      organization: formData.organization || ''
    });
    
    try {
      const response = await fetch(`${API_URL}/api/diagnostic-tool/questions?exam_type=${examType}&count=30`);
      if (!response.ok) throw new Error('Failed to fetch questions');
      const data = await response.json();
      setQuestions(data.questions);
      setStage('quiz');
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizComplete = async (answers) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/diagnostic-tool/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exam_type: examType,
          answers: answers,
          user_info: userInfo
        })
      });
      if (!response.ok) throw new Error('Failed to submit exam');
      const data = await response.json();
      setResults(data);
      setStage('results');
    } catch (err) {
      console.error('Error submitting exam:', err);
      setError('Failed to submit exam. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setStage('select');
    setExamType(null);
    setQuestions([]);
    setResults(null);
    setUserInfo(null);
  };

  const handleBackToSelect = () => {
    if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
      setStage('select');
      setExamType(null);
      setQuestions([]);
      setUserInfo(null);
    }
  };

  const handleBackFromRegister = () => {
    setStage('select');
    setExamType(null);
  };

  if (error) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div>
          <XCircle size={48} color="#dc2626" style={{ marginBottom: '20px' }} />
          <h2 style={{ marginBottom: '10px' }}>Something went wrong</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: 'var(--brand-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="diagnostic-tool-page">
      <SEOHead />
      
      {stage === 'select' && (
        <ExamSelector 
          onSelectExam={handleSelectExam} 
          examInfo={examInfo}
          loading={loading}
        />
      )}
      
      {stage === 'register' && (
        <UserRegistrationForm
          examType={examType}
          examName={examInfo?.exams?.find(e => e.id === examType)?.name}
          onSubmit={handleUserRegistration}
          onBack={handleBackFromRegister}
          loading={loading}
        />
      )}
      
      {stage === 'quiz' && questions.length > 0 && (
        <QuizInterface 
          examType={examType}
          questions={questions}
          onComplete={handleQuizComplete}
          onBack={handleBackToSelect}
        />
      )}
      
      {stage === 'results' && results && (
        <ResultsDisplay 
          results={results}
          examType={examType}
          examInfo={examInfo}
          onRetake={handleRetake}
        />
      )}
      
      {loading && stage !== 'select' && stage !== 'register' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ textAlign: 'center' }}>
            <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: 'var(--brand-primary)' }} />
            <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>
              {stage === 'quiz' ? 'Loading questions...' : 'Processing your results...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosticTool;
